/* ============================================================
 * <lion-delivery-estimate> — PDP block + lion-cart-drawer item.
 *
 * Computes an estimated delivery date range from "now" + min/max
 * business days, optionally skipping weekends. Uses Intl.DateTimeFormat
 * for locale-aware formatting (picked up from document <html lang>).
 *
 * Attributes (all optional, sensible defaults):
 *   data-min-days        Minimum business days to deliver (default 3)
 *   data-max-days        Maximum business days to deliver (default 7)
 *   data-cutoff-hour     Order cutoff hour (0-23). Orders after this
 *                        hour count from tomorrow. Default 14.
 *   data-skip-weekends   "true" to skip Sat/Sun. Default "true".
 *   data-locale          BCP-47 locale (default: document.documentElement.lang
 *                        or "en").
 *   data-template        Output template with {start} / {end} placeholders.
 *                        Default "{start} – {end}".
 *
 * Notes:
 *   - No setInterval: dates are stable for the page-view, recomputed
 *     only on connectedCallback.
 *   - Holidays are NOT subtracted — that would require a calendar source.
 *   - Server-rendered fallback text inside the element is shown if JS
 *     fails / is disabled (graceful degradation).
 * ============================================================ */

class DeliveryEstimate extends HTMLElement {
  connectedCallback() {
    if (this.dataset.rendered === '1') return; // idempotent
    try {
      const minDays = Math.max(0, intOr(this.dataset.minDays, 3));
      const maxDaysRaw = Math.max(0, intOr(this.dataset.maxDays, 7));
      // Clamp: if max < min, fallback to min (single date).
      const maxDays = Math.max(minDays, maxDaysRaw);
      const cutoffHour = clamp(parseInt(this.dataset.cutoffHour, 10), 0, 23, 14);
      const skipWeekends = this.dataset.skipWeekends !== 'false';
      const locale = this.dataset.locale || document.documentElement.lang || 'en';
      const template = this.dataset.template || '{start} – {end}';

      const now = new Date();
      // If past cutoff hour, start counting from tomorrow.
      const startOffset = now.getHours() >= cutoffHour ? 1 : 0;

      const minDate = addBusinessDays(now, minDays + startOffset, skipWeekends);
      const maxDate = addBusinessDays(now, maxDays + startOffset, skipWeekends);

      const fmt = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', weekday: 'short' });
      const startStr = fmt.format(minDate);
      const endStr = minDate.getTime() === maxDate.getTime() ? '' : fmt.format(maxDate);

      const output = endStr
        ? template.replace('{start}', startStr).replace('{end}', endStr)
        : startStr;

      const target = this.querySelector('[data-delivery-output]');
      if (target) target.textContent = output;
      this.dataset.rendered = '1';
    } catch (e) {
      // Keep the server-rendered fallback intact on any error.
      console.warn('[lion-delivery-estimate] render failed:', e);
    }
  }
}

function clamp(n, min, max, fallback) {
  if (!Number.isFinite(n)) return fallback;
  return Math.max(min, Math.min(max, n));
}

// parseInt with a finite-aware fallback: avoids the "0 is falsy" trap that
// `parseInt(x) || fb` falls into when 0 is a legitimate user value.
function intOr(raw, fallback) {
  const n = parseInt(raw, 10);
  return Number.isFinite(n) ? n : fallback;
}

function addBusinessDays(start, n, skipWeekends) {
  const d = new Date(start.getTime());
  d.setHours(0, 0, 0, 0);
  if (n <= 0) return d;
  let added = 0;
  while (added < n) {
    d.setDate(d.getDate() + 1);
    if (!skipWeekends) {
      added++;
    } else {
      const dow = d.getDay(); // 0=Sun, 6=Sat
      if (dow !== 0 && dow !== 6) added++;
    }
  }
  return d;
}

if (!customElements.get('lion-delivery-estimate')) {
  customElements.define('lion-delivery-estimate', DeliveryEstimate);
}
